// src/pages/History.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, orderBy, limit, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from "../services/firebase";
import { useAuth } from "../contexts/AuthContext";
import { PLANS } from "../services/payment";
import "./History.css";

function timeAgo(date) {
  const diff  = Date.now() - date.getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  <  60) return `${mins}m ago`;
  if (hours <  24) return `${hours}h ago`;
  if (days  <  30) return `${days}d ago`;
  return date.toLocaleDateString();
}

// Build YouTube thumbnail from videoId
function thumbUrl(videoId) {
  return videoId
    ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
    : null;
}

export default function History() {
  const { user, userData } = useAuth();
  const navigate           = useNavigate();
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [deleting, setDeleting] = useState(null);
  const [error, setError]       = useState(null);

  const plan        = userData?.plan || "free";
  const planConfig  = PLANS[plan] || PLANS.free;
  const historyLimit = planConfig.historyLimit; // null = unlimited, 3 = free

  useEffect(() => {
    if (user) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      // ── Firestore query ───────────────────────────────────
      // Collection: "analyses"
      // Each doc should have: uid, videoId, videoTitle, commentsCount,
      //                       ideasCount, repliesCount, createdAt (Timestamp)
      // Free plan: only fetch last 3; Pro: fetch all
      let q = query(
        collection(db, "analyses"),
        where("uid", "==", user.uid),
        orderBy("createdAt", "desc"),
        ...(historyLimit ? [limit(historyLimit)] : [])
      );

      const snap  = await getDocs(q);
      const items = snap.docs.map(d => {
        const data = d.data();
        return {
          id:            d.id,
          videoId:       data.videoId       || "",
          videoTitle:    data.videoTitle     || data.videoId || "Untitled Video",
          commentsCount: data.commentsCount  || 0,
          ideasCount:    data.ideasCount     || (data.insights?.ideas?.length ?? 0),
          repliesCount:  data.repliesCount   || (data.insights?.replies?.length ?? 0),
          analyzedAt:    data.createdAt?.toDate?.() || new Date(),
          source:        data.source         || "",
        };
      });

      setHistory(items);
    } catch (err) {
      console.error("Failed to load history:", err);
      setError("Could not load history. Check Firestore rules or your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {

    const confirmed = window.confirm(
      "Are you sure you want to delete this analysis?"
    );

    if (!confirmed) return;
    
    setDeleting(id);
    try {
      await deleteDoc(doc(db, "analyses", id));
      setHistory(prev => prev.filter(h => h.id !== id));
    } catch (err) {
      console.error("Failed to delete:", err);
    } finally {
      setDeleting(null);
    }
  };

  const filtered = history.filter(h =>
    h.videoTitle.toLowerCase().includes(search.toLowerCase()) ||
    h.videoId.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="page-wrap history-page">

      {/* ─── Header ─── */}
      <div className="history-header fade-up">
        <div>
          <h1 className="history-title">Analysis History</h1>
          <p className="history-sub">
            {history.length} video{history.length !== 1 ? "s" : ""} analyzed
            {historyLimit && (
              <span className="history-limit-note"> · Free plan shows last {historyLimit} only</span>
            )}
          </p>
        </div>
        <input
          type="text"
          placeholder="Search videos..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="history-search"
        />
      </div>

      {/* ─── Free plan upgrade nudge ─── */}
      {plan === "free" && history.length >= 3 && (
        <div className="history-upgrade-nudge fade-up">
          <span>🔒 Free plan shows only your last 3 analyses.</span>
          <button className="btn btn-primary" style={{ fontSize: 13, padding: "7px 14px" }}
            onClick={() => navigate("/settings/#plan")}>
            ✦ Upgrade for full history
          </button>
        </div>
      )}

      {/* ─── Stats Strip ─── */}
      <div className="stats-strip fade-up">
        {[
          { label: "Videos Analyzed",  value: history.length },
          { label: "Total Comments",   value: history.reduce((a, h) => a + h.commentsCount, 0).toLocaleString() },
          { label: "Ideas Generated",  value: history.reduce((a, h) => a + h.ideasCount, 0) },
          { label: "Replies Written",  value: history.reduce((a, h) => a + h.repliesCount, 0) },
        ].map(stat => (
          <div key={stat.label} className="stat-chip card">
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* ─── Error ─── */}
      {error && (
        <div className="history-error fade-up">⚠️ {error}</div>
      )}

      {/* ─── List ─── */}
      {loading ? (
        <div className="history-list">
          {[1, 2, 3].map(i => (
            <div key={i} className="card history-skeleton">
              <div className="shimmer" style={{ width: 140, height: 80, borderRadius: 8, flexShrink: 0 }} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
                <div className="shimmer" style={{ height: 18, width: "60%", borderRadius: 6 }} />
                <div className="shimmer" style={{ height: 14, width: "40%", borderRadius: 6 }} />
                <div className="shimmer" style={{ height: 14, width: "30%", borderRadius: 6 }} />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 && !error ? (
        <div className="history-empty fade-up">
          <div style={{ fontSize: 48 }}>{search ? "🔍" : "📭"}</div>
          <h3>{search ? "No videos match your search" : "No history yet"}</h3>
          <p>{search ? "Try a different keyword" : "Analyze your first YouTube video to see it here"}</p>
          {!search && (
            <button className="btn btn-primary" onClick={() => navigate("/dashboard")}>
              ⚡ Analyze a Video
            </button>
          )}
        </div>
      ) : (
        <div className="history-list fade-up">
          {filtered.map((item, i) => (
            <div key={item.id} className="history-card card" style={{ animationDelay: `${i * 0.05}s` }}>

              {/* Thumbnail from YouTube CDN */}
              {thumbUrl(item.videoId) ? (
                <img
                  src={thumbUrl(item.videoId)}
                  alt={item.videoTitle}
                  className="history-thumb"
                  onError={e => { e.target.style.display = "none"; }}
                />
              ) : (
                <div className="history-thumb-placeholder">🎬</div>
              )}

              <div className="history-info">
                <h3 className="history-video-title">{item.videoTitle}</h3>
                <div className="history-meta">
                  {item.videoId && (
                    <span>
                      <a
                        href={`https://www.youtube.com/watch?v=${item.videoId}`}
                        target="_blank"
                        rel="noreferrer"
                        className="yt-link"
                      >
                        ▶ youtube.com/watch?v={item.videoId}
                      </a>
                    </span>
                  )}
                  <span>🕐 {timeAgo(item.analyzedAt)}</span>
                  <span>💬 {item.commentsCount.toLocaleString()} comments</span>
                </div>
                <div className="history-badges">
                  {item.ideasCount  > 0 && <span className="badge badge-accent">💡 {item.ideasCount} ideas</span>}
                  {item.repliesCount > 0 && <span className="badge badge-green">💬 {item.repliesCount} replies</span>}
                  {/* {item.source === "cache" && <span className="badge" style={{ background: "var(--surface2)", color: "var(--text-dim)" }}>⚡ cached</span>} */}
                </div>
              </div>

              <div className="history-actions">
                <button
                    className="btn btn-primary"
                    style={{ fontSize: 13, padding: "8px 14px" }}
                    onClick={() => navigate(`/dashboard?videoid=${item.videoId}`)}
                  >
                    View Analysis ↗
                  </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleDelete(item.id)}
                  disabled={deleting === item.id}
                  style={{ fontSize: 13, padding: "8px 14px" }}
                >
                  {deleting === item.id ? "..." : "Delete"}
                </button>
              </div>

            </div>
          ))}
        </div>
      )}
    </div>
  );
}